import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import {
    collection, getDocs, getDoc, doc, setDoc, updateDoc,
    addDoc, query, where, orderBy, Timestamp, deleteDoc
} from 'firebase/firestore';
import { auth, db } from './firebase';
import {
    Phone, MessageCircle, Plus, X, ChevronDown, Search,
    Users, TrendingUp, FileText, CheckCircle, AlertCircle,
    RefreshCw, Eye, Menu, ChevronLeft, ChevronRight, Pencil,
    Upload
} from 'lucide-react';
import DashboardNav from './DashboardNav';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
    { value: 'new',            label: 'New',            color: 'bg-blue-500',    text: 'text-blue-600',    dot: 'bg-blue-400' },
    { value: 'contacted',      label: 'Contacted',      color: 'bg-yellow-500',  text: 'text-yellow-600',  dot: 'bg-yellow-400' },
    { value: 'interested',     label: 'Interested',     color: 'bg-orange-500',  text: 'text-orange-600',  dot: 'bg-orange-400' },
    { value: 'followup',       label: 'Follow Up',      color: 'bg-purple-500',  text: 'text-purple-600',  dot: 'bg-purple-400' },
    { value: 'converted',      label: 'Converted',      color: 'bg-emerald-500', text: 'text-emerald-600', dot: 'bg-emerald-400' },
    { value: 'not_interested', label: 'Not Interested', color: 'bg-red-500',     text: 'text-red-600',     dot: 'bg-red-400' },
    { value: 'lost',           label: 'Lost',           color: 'bg-gray-500',    text: 'text-gray-600',    dot: 'bg-gray-400' },
];

const SOURCE_OPTIONS = [
    { value: 'whatsapp',   label: 'WhatsApp',   icon: '💬' },
    { value: 'messenger',  label: 'Messenger',  icon: '📱' },
    { value: 'over_phone', label: 'Over Phone', icon: '📞' },
    { value: 'facebook',   label: 'Facebook',   icon: '𝑓' },
    { value: 'instagram',  label: 'Instagram',  icon: '◎' },
    { value: 'other',      label: 'Other',      icon: '🔗' },
];

const ITEMS_PER_PAGE = 15;
const BULK_API_URL   = 'https://elevatemart-lead-api.sparkx-ai.shop/api/add-phone';
const BULK_API_KEY   = 'elevatemart-secret-key';
// Server allows max 100 per request, rate limit is 60 req/min
// So we send all numbers in ONE request (max 100) or split into chunks with delay
const BATCH_SIZE     = 100;

const getStatusCfg = (v) => STATUS_OPTIONS.find(s => s.value === v) || STATUS_OPTIONS[0];

// ─── Parse raw phone text → clean 11-digit BD numbers ─────────────────────────
const parsePhones = (raw) => {
    const tokens = raw.match(/[\d\s\-+]+/g) || [];
    const result = [];
    for (const t of tokens) {
        const digits = t.replace(/\D/g, '');
        if (digits.length === 13 && digits.startsWith('880')) {
            result.push('0' + digits.slice(3));
        } else if (digits.length === 12 && digits.startsWith('880')) {
            result.push('0' + digits.slice(3));
        } else if (digits.length === 11 && digits.startsWith('01')) {
            result.push(digits);
        }
    }
    return [...new Set(result)];
};

// ─── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ toast }) => {
    if (!toast) return null;
    return (
        <div className={`fixed top-5 right-5 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-xl
            shadow-md text-sm font-semibold border
            ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
            {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
            {toast.msg}
        </div>
    );
};

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, color }) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
            {icon}
        </div>
        <div>
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <p className="text-xl font-black text-gray-900">{value}</p>
        </div>
    </div>
);

// ─── Pagination Component ──────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem   = Math.min(currentPage * itemsPerPage, totalItems);

    const getPages = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    if (totalPages <= 1) return (
        <p className="text-xs text-gray-500 py-1">
            Showing <span className="text-gray-900 font-semibold">{totalItems}</span> lead{totalItems !== 1 ? 's' : ''}
        </p>
    );

    return (
        <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
                Showing <span className="text-gray-900 font-semibold">{startItem}–{endItem}</span> of{' '}
                <span className="text-gray-900 font-semibold">{totalItems}</span> leads
            </p>
            <div className="flex items-center gap-1">
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition text-gray-500">
                    <ChevronLeft size={16} />
                </button>
                {getPages().map((p, i) =>
                    p === '...'
                        ? <span key={`e${i}`} className="px-2 text-gray-500 text-sm select-none">…</span>
                        : <button key={p} onClick={() => onPageChange(p)}
                            className={`w-8 h-8 rounded-lg text-sm font-semibold transition
                                ${currentPage === p ? 'bg-orange-500 text-white' : 'hover:bg-gray-100 text-gray-500'}`}>
                            {p}
                          </button>
                )}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition text-gray-500">
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function LeadManagerPage() {
    const navigate = useNavigate();
    const [userEmail,    setUserEmail]    = useState('');
    const [sidebarOpen,  setSidebarOpen]  = useState(() => typeof window === 'undefined' || window.innerWidth >= 1024);

    // Data
    const [allLeads,  setAllLeads]  = useState([]);
    const [products,  setProducts]  = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [toast,     setToast]     = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Filters
    const [searchPhone,   setSearchPhone]   = useState('');
    const [filterStatus,  setFilterStatus]  = useState('');
    const [filterProduct, setFilterProduct] = useState('');

    // Add number
    const [showAddModal, setShowAddModal] = useState(false);
    const [newPhone,     setNewPhone]     = useState('');
    const [addingPhone,  setAddingPhone]  = useState(false);

    // ── Bulk Import state ──────────────────────────────────────────────────────
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkText,      setBulkText]      = useState('');
    const [bulkRunning,   setBulkRunning]   = useState(false);
    const [bulkProgress,  setBulkProgress]  = useState(0);   // batches done
    const [bulkTotal,     setBulkTotal]     = useState(0);   // total batches
    const [bulkResults,   setBulkResults]   = useState(null);
    const [bulkStatusMsg, setBulkStatusMsg] = useState('');

    // Edit phone (once only)
    const [editModal,    setEditModal]    = useState(null);
    const [editPhoneVal, setEditPhoneVal] = useState('');
    const [savingEdit,   setSavingEdit]   = useState(false);

    // Detail panel
    const [selectedLead,  setSelectedLead]  = useState(null);
    const [leadDetail,    setLeadDetail]    = useState(null);
    const [notes,         setNotes]         = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    // Note modal
    const [noteModal,  setNoteModal]  = useState(null);
    const [noteText,   setNoteText]   = useState('');
    const [savingNote, setSavingNote] = useState(false);

    // Dropdowns in detail panel
    const [productDropdownOpen, setProductDropdownOpen] = useState(false);
    const [statusDropdownOpen,  setStatusDropdownOpen]  = useState(false);
    const [sourceDropdownOpen,  setSourceDropdownOpen]  = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    // ── Auth ───────────────────────────────────────────────────────────────────
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) setUserEmail(user.email);
            else navigate('/login');
        });
        return unsub;
    }, [navigate]);

    // ── Fetch products ─────────────────────────────────────────────────────────
    useEffect(() => {
        getDocs(collection(db, 'products'))
            .then(snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    }, []);

    // ── Fetch all leads ────────────────────────────────────────────────────────
    const fetchLeads = useCallback(async () => {
        setLoading(true);
        try {
            const adminSnap = await getDocs(collection(db, 'admins'));
            const allPhones = [];
            for (const adminDoc of adminSnap.docs) {
                const phoneSnap = await getDocs(
                    query(collection(db, 'admins', adminDoc.id, 'phoneNumbers'), orderBy('addedAt', 'desc'))
                );
                phoneSnap.docs.forEach(pd => {
                    allPhones.push({
                        phone:      pd.id,
                        adminId:    adminDoc.id,
                        adminEmail: adminDoc.data()?.email || adminDoc.id,
                        ...pd.data(),
                    });
                });
            }
            const seen = new Set();
            setAllLeads(allPhones.filter(p => {
                if (seen.has(p.phone)) return false;
                seen.add(p.phone);
                return true;
            }));
        } catch (e) {
            showToast('Failed to load leads: ' + e.message, 'error');
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    // Reset page on filter change
    useEffect(() => { setCurrentPage(1); }, [searchPhone, filterStatus, filterProduct]);

    // ── Filtered + paginated ───────────────────────────────────────────────────
    const filteredLeads = allLeads.filter(l => {
        if (searchPhone   && !l.phone.includes(searchPhone))  return false;
        if (filterStatus  && l.status !== filterStatus)        return false;
        if (filterProduct && !(l.interestedProducts || []).some(p => p.productId === filterProduct)) return false;
        return true;
    });

    const totalPages = Math.ceil(filteredLeads.length / ITEMS_PER_PAGE);
    const pagedLeads = filteredLeads.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    // ── Stats ──────────────────────────────────────────────────────────────────
    const stats          = STATUS_OPTIONS.map(s => ({ ...s, count: allLeads.filter(l => (l.status || 'new') === s.value).length }));
    const convertedCount = allLeads.filter(l => l.status === 'converted').length;

    // ── Add phone ──────────────────────────────────────────────────────────────
    const handleAddPhone = async () => {
        const phone = newPhone.trim();
        if (phone.length !== 11 || !/^\d+$/.test(phone)) return showToast('Phone number must be exactly 11 digits', 'error');
        if (allLeads.some(l => l.phone === phone))         return showToast('This number already exists', 'error');
        if (!userEmail)                                    return showToast('Not logged in', 'error');

        setAddingPhone(true);
        try {
            const adminQ    = query(collection(db, 'admins'), where('email', '==', userEmail));
            const adminSnap = await getDocs(adminQ);
            if (adminSnap.empty) { showToast('Admin record not found for your email', 'error'); setAddingPhone(false); return; }
            const adminId = adminSnap.docs[0].id;

            await setDoc(doc(db, 'admins', adminId, 'phoneNumbers', phone), {
                number:  phone,
                addedAt: Timestamp.now(),
                addedBy: userEmail,
                status:  'new',
                edited:  false,
            });
            showToast('Phone number added!');
            setNewPhone('');
            setShowAddModal(false);
            fetchLeads();
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        setAddingPhone(false);
    };

    // ── Bulk Import — batched, respects rate limit ─────────────────────────────
    // Server: max 100 phones/request, rate limit 60 req/min
    // Strategy: send BATCH_SIZE (100) at a time, wait 1100ms between batches
    // So 200 numbers = 2 requests, takes ~1.1s extra — well within rate limit
    const handleBulkImport = async () => {
        const phones = parsePhones(bulkText);
        if (phones.length === 0) return showToast('কোনো valid নাম্বার পাওয়া যায়নি', 'error');

        // Split into batches of 100
        const batches = [];
        for (let i = 0; i < phones.length; i += BATCH_SIZE) {
            batches.push(phones.slice(i, i + BATCH_SIZE));
        }

        setBulkRunning(true);
        setBulkTotal(batches.length);
        setBulkProgress(0);
        setBulkResults(null);
        setBulkStatusMsg('');

        const results = { success: [], failed: [], skipped: [] };

        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            setBulkStatusMsg(`Batch ${i + 1} / ${batches.length} (${batch.length} নাম্বার)...`);

            try {
                const res = await fetch(BULK_API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': BULK_API_KEY,
                    },
                    body: JSON.stringify({ phones: batch }),
                });

                const data = await res.json().catch(() => ({}));

                if (res.ok && data.success) {
                    // Server returns: data.results.added (string[]), .skipped [{phone,reason}], .failed [{phone,reason}]
                    (data.results?.added   || []).forEach(phone => results.success.push({ phone }));
                    (data.results?.skipped || []).forEach(item  => {
                        const phone  = typeof item === 'string' ? item : item.phone;
                        const reason = typeof item === 'string' ? 'already exists' : (item.reason || 'already exists');
                        results.skipped.push({ phone, reason });
                    });
                    (data.results?.failed  || []).forEach(item  => {
                        const phone  = typeof item === 'string' ? item : item.phone;
                        const reason = typeof item === 'string' ? 'unknown' : (item.reason || 'unknown');
                        results.failed.push({ phone, reason });
                    });
                } else if (res.status === 429) {
                    // Rate limited — wait 62 seconds then retry this batch
                    setBulkStatusMsg(`Rate limit hit — 62 সেকেন্ড অপেক্ষা করছি...`);
                    await new Promise(r => setTimeout(r, 62000));
                    // Retry same batch
                    i--;
                    setBulkProgress(Math.max(0, i));
                    continue;
                } else {
                    const reason = data?.error || `HTTP ${res.status}`;
                    batch.forEach(phone => results.failed.push({ phone, reason }));
                }
            } catch (err) {
                batch.forEach(phone => results.failed.push({ phone, reason: err.message }));
            }

            setBulkProgress(i + 1);

            // Wait 1.1s between batches to stay within 60 req/min rate limit
            if (i < batches.length - 1) {
                setBulkStatusMsg(`Batch ${i + 1} done — পরের batch এর জন্য অপেক্ষা করছি...`);
                await new Promise(r => setTimeout(r, 1100));
            }
        }

        setBulkStatusMsg('');
        setBulkResults(results);
        setBulkRunning(false);

        if (results.success.length > 0) {
            showToast(`✅ ${results.success.length} নাম্বার সফলভাবে add হয়েছে!`);
            fetchLeads();
        }
    };

    const closeBulkModal = () => {
        if (bulkRunning) return;
        setShowBulkModal(false);
        setBulkText('');
        setBulkResults(null);
        setBulkProgress(0);
        setBulkTotal(0);
        setBulkStatusMsg('');
    };

    // ── Open edit modal ────────────────────────────────────────────────────────
    const openEditModal = (lead, e) => {
        e.stopPropagation();
        if (lead.edited) return showToast('This number has already been edited once', 'error');
        setEditModal(lead);
        setEditPhoneVal(lead.phone);
    };

    // ── Save phone edit ────────────────────────────────────────────────────────
    const handleEditPhone = async () => {
        const newVal = editPhoneVal.trim();
        if (newVal.length !== 11 || !/^\d+$/.test(newVal)) return showToast('Phone number must be exactly 11 digits', 'error');
        if (newVal === editModal.phone) { setEditModal(null); return; }
        if (allLeads.some(l => l.phone === newVal))         return showToast('This number already exists', 'error');

        setSavingEdit(true);
        try {
            const oldRef  = doc(db, 'admins', editModal.adminId, 'phoneNumbers', editModal.phone);
            const oldSnap = await getDoc(oldRef);
            const oldData = oldSnap.data() || {};

            await setDoc(doc(db, 'admins', editModal.adminId, 'phoneNumbers', newVal), {
                ...oldData,
                number:   newVal,
                edited:   true,
                editedAt: Timestamp.now(),
                editedBy: userEmail,
            });

            const notesSnap = await getDocs(collection(db, 'admins', editModal.adminId, 'phoneNumbers', editModal.phone, 'notes'));
            for (const nd of notesSnap.docs) {
                await setDoc(doc(db, 'admins', editModal.adminId, 'phoneNumbers', newVal, 'notes', nd.id), nd.data());
            }

            await deleteDoc(oldRef);

            showToast('Phone number updated!');
            if (selectedLead?.phone === editModal.phone) setSelectedLead(null);
            setEditModal(null);
            setEditPhoneVal('');
            fetchLeads();
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        setSavingEdit(false);
    };

    // ── Open lead detail ───────────────────────────────────────────────────────
    const openLead = async (lead) => {
        setSelectedLead(lead);
        setDetailLoading(true);
        setLeadDetail(null);
        setNotes([]);
        setProductDropdownOpen(false);
        setStatusDropdownOpen(false);
        setSourceDropdownOpen(false);
        try {
            const snap = await getDoc(doc(db, 'admins', lead.adminId, 'phoneNumbers', lead.phone));
            setLeadDetail(snap.exists() ? { id: snap.id, ...snap.data() } : { id: lead.phone });

            const notesSnap = await getDocs(
                query(collection(db, 'admins', lead.adminId, 'phoneNumbers', lead.phone, 'notes'), orderBy('createdAt', 'desc'))
            );
            setNotes(notesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { showToast('Error loading details: ' + e.message, 'error'); }
        setDetailLoading(false);
    };

    // ── Update a field on the lead doc ─────────────────────────────────────────
    const updateLeadField = async (field, value) => {
        if (!selectedLead) return;
        await updateDoc(doc(db, 'admins', selectedLead.adminId, 'phoneNumbers', selectedLead.phone), { [field]: value, updatedAt: Timestamp.now() });
        setLeadDetail(prev => ({ ...prev, [field]: value }));
        setAllLeads(prev => prev.map(l => l.phone === selectedLead.phone ? { ...l, [field]: value } : l));
    };

    // ── Toggle interested product ──────────────────────────────────────────────
    const toggleProduct = async (product) => {
        if (!selectedLead || !leadDetail) return;
        const current = leadDetail.interestedProducts || [];
        const exists  = current.find(p => p.productId === product.productId);
        const updated = exists
            ? current.filter(p => p.productId !== product.productId)
            : [...current, { productId: product.productId, name: product.name, image: product.image, price: product.price }];
        await updateLeadField('interestedProducts', updated);
    };

    // ── Save note ──────────────────────────────────────────────────────────────
    const handleNoteSubmit = async () => {
        if (!noteText.trim()) return showToast('Note cannot be empty', 'error');
        if (!selectedLead) return;
        setSavingNote(true);
        try {
            const countField = noteModal.type === 'call' ? 'callCount' : 'whatsappCount';
            await updateLeadField(countField, (leadDetail?.[countField] || 0) + 1);
            const newNote  = { type: noteModal.type, note: noteText, createdBy: userEmail, createdAt: Timestamp.now() };
            const added    = await addDoc(collection(db, 'admins', selectedLead.adminId, 'phoneNumbers', selectedLead.phone, 'notes'), newNote);
            setNotes(prev => [{ id: added.id, ...newNote }, ...prev]);
            setNoteModal(null);
            setNoteText('');
            showToast('Note saved!');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
        setSavingNote(false);
    };

    const formatTime = (ts) => {
        if (!ts) return '';
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleString('en-BD', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const previewPhones = bulkText ? parsePhones(bulkText) : [];
    const previewBatches = Math.ceil(previewPhones.length / BATCH_SIZE);

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="flex min-h-screen bg-gray-50 text-gray-900">
            <Toast toast={toast} />
            <DashboardNav sidebarOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

            <div className="flex-1 min-w-0 overflow-auto">

                {/* ── Header ── */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 sticky top-0 z-20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-orange-50 rounded-lg transition">
                                <Menu size={22} />
                            </button>
                            <div>
                                <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2">
                                    <Users size={22} className="text-orange-600" /> Lead Manager
                                </h1>
                                <p className="text-xs text-gray-500 mt-0.5">Logged in as: {userEmail}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button onClick={() => setShowBulkModal(true)}
                                className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg text-sm font-semibold transition">
                                <Upload size={15} /> Bulk Import
                            </button>
                            <button onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2 bg-orange-400 text-white hover:bg-orange-500 px-4 py-2 rounded-lg text-sm font-semibold transition">
                                <Plus size={16} /> Add Number
                            </button>
                            <button onClick={fetchLeads} title="Refresh" className="p-2 hover:bg-orange-50 rounded-lg transition text-gray-500">
                                <RefreshCw size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-6 max-w-[1600px] mx-auto">

                    {/* ── Stats ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mb-6">
                        <div className="col-span-2 lg:col-span-1">
                            <StatCard icon={<TrendingUp size={18} />} label="Total Leads" value={allLeads.length} color="bg-orange-500 text-white" />
                        </div>
                        <div className="col-span-2 lg:col-span-1">
                            <StatCard icon={<CheckCircle size={18} />} label="Converted" value={convertedCount} color="bg-emerald-100 text-emerald-700" />
                        </div>
                        {stats.map(s => (
                            <div key={s.value} className="bg-white border border-gray-200 rounded-xl p-3 text-center">
                                <div className={`inline-block w-2 h-2 rounded-full ${s.dot} mb-1`} />
                                <p className="text-[10px] text-gray-500 font-medium leading-tight truncate px-1">{s.label}</p>
                                <p className="text-lg font-black text-gray-900">{s.count}</p>
                            </div>
                        ))}
                    </div>

                    {/* ── Filters ── */}
                    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[180px]">
                            <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-1.5">Search Phone</label>
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                <input value={searchPhone} onChange={e => setSearchPhone(e.target.value)}
                                    placeholder="01XXXXXXXXX"
                                    className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-500" />
                            </div>
                        </div>
                        <div className="min-w-[150px]">
                            <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-1.5">Status</label>
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 outline-none focus:border-orange-500">
                                <option value="">All Status</option>
                                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </div>
                        <div className="min-w-[180px]">
                            <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-1.5">Product</label>
                            <select value={filterProduct} onChange={e => setFilterProduct(e.target.value)}
                                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 outline-none focus:border-orange-500">
                                <option value="">All Products</option>
                                {products.map(p => <option key={p.productId} value={p.productId}>{p.name}</option>)}
                            </select>
                        </div>
                        <button onClick={() => { setSearchPhone(''); setFilterStatus(''); setFilterProduct(''); }}
                            className="px-4 py-2 bg-orange-100 text-orange-700 hover:bg-orange-200 text-sm rounded-lg transition">
                            Clear
                        </button>
                        <span className="text-xs text-gray-500 self-end pb-2 ml-auto">
                            {filteredLeads.length} of {allLeads.length} leads
                        </span>
                    </div>

                    {/* ── Table + Detail Panel ── */}
                    <div className="flex gap-6">

                        {/* Table */}
                        <div className={`${selectedLead ? 'w-1/2' : 'w-full'} transition-all duration-300 min-w-0`}>
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                {loading ? (
                                    <div className="flex justify-center items-center h-48">
                                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-orange-500" />
                                    </div>
                                ) : filteredLeads.length === 0 ? (
                                    <div className="text-center py-16 text-gray-500">
                                        <Users size={40} className="mx-auto mb-3 opacity-30" />
                                        <p>No leads found</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        {['#', 'Phone', 'Status', 'Source', 'Products', 'Calls', 'WA', 'Notes', 'Added By', ''].map(h => (
                                                            <th key={h} className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {pagedLeads.map((lead, idx) => {
                                                        const sc       = getStatusCfg(lead.status || 'new');
                                                        const src      = SOURCE_OPTIONS.find(s => s.value === lead.source);
                                                        const isActive = selectedLead?.phone === lead.phone;
                                                        const rowNum   = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
                                                        return (
                                                            <tr key={lead.phone}
                                                                onClick={() => openLead(lead)}
                                                                className={`hover:bg-orange-50 transition cursor-pointer
                                                                    ${isActive ? 'bg-orange-50 border-l-2 border-l-indigo-500' : ''}`}>
                                                                <td className="px-4 py-3 text-xs text-gray-500 font-mono">{rowNum}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-mono font-bold text-gray-900">{lead.phone}</span>
                                                                        {lead.edited && (
                                                                            <span className="text-[9px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full font-semibold">edited</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-gray-50 ${sc.text} whitespace-nowrap`}>
                                                                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />{sc.label}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{src ? `${src.icon} ${src.label}` : '—'}</td>
                                                                <td className="px-4 py-3 whitespace-nowrap">
                                                                    {(lead.interestedProducts || []).length > 0
                                                                        ? <span className="text-xs text-orange-600 font-semibold">{lead.interestedProducts.length} item{lead.interestedProducts.length > 1 ? 's' : ''}</span>
                                                                        : <span className="text-gray-500 text-xs">—</span>}
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="inline-flex items-center gap-1 text-xs bg-gray-50 px-2 py-1 rounded-full whitespace-nowrap">
                                                                        <Phone size={10} className="text-green-600" />{lead.callCount || 0}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="inline-flex items-center gap-1 text-xs bg-gray-50 px-2 py-1 rounded-full whitespace-nowrap">
                                                                        <MessageCircle size={10} className="text-orange-600" />{lead.whatsappCount || 0}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className="text-xs text-gray-500">{lead.noteCount || 0}</span>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <span className="text-[11px] text-gray-500 truncate max-w-[90px] block">{lead.addedBy || lead.adminEmail}</span>
                                                                </td>
                                                                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <button onClick={(e) => openEditModal(lead, e)}
                                                                            title={lead.edited ? 'Already edited once' : 'Edit phone (once only)'}
                                                                            className={`p-1.5 rounded-lg transition ${lead.edited ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50'}`}>
                                                                            <Pencil size={13} />
                                                                        </button>
                                                                        <button onClick={(e) => { e.stopPropagation(); openLead(lead); }}
                                                                            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition">
                                                                            <Eye size={13} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="border-t border-gray-200 px-4 py-3">
                                            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filteredLeads.length} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Detail Panel ── */}
                        {selectedLead && (
                            <div className="w-1/2 flex-shrink-0">
                                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden sticky top-24">
                                    <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex items-center justify-between">
                                        <div>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Lead Details</p>
                                            <p className="text-lg font-black font-mono text-gray-900">{selectedLead.phone}</p>
                                        </div>
                                        <button onClick={() => setSelectedLead(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-500"><X size={18} /></button>
                                    </div>
                                    {detailLoading ? (
                                        <div className="flex justify-center items-center h-32">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
                                        </div>
                                    ) : (
                                        <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                                            <div className="p-5 space-y-5">
                                                <div className="flex gap-2">
                                                    <button onClick={() => setNoteModal({ type: 'call' })}
                                                        className="flex-1 flex items-center justify-center gap-2 bg-green-50 border border-green-200 hover:bg-green-50 text-green-600 text-sm font-semibold py-2.5 rounded-xl transition">
                                                        <Phone size={15} /> Call
                                                        <span className="bg-green-50 text-xs px-2 py-0.5 rounded-full">{leadDetail?.callCount || 0}</span>
                                                    </button>
                                                    <a href={`https://wa.me/${selectedLead.phone.replace(/^0/, '880')}`} target="_blank" rel="noreferrer"
                                                        onClick={() => setNoteModal({ type: 'whatsapp' })}
                                                        className="flex-1 flex items-center justify-center gap-2 bg-orange-50 border border-orange-200 hover:bg-orange-50 text-orange-600 text-sm font-semibold py-2.5 rounded-xl transition">
                                                        <MessageCircle size={15} /> WhatsApp
                                                        <span className="bg-orange-50 text-xs px-2 py-0.5 rounded-full">{leadDetail?.whatsappCount || 0}</span>
                                                    </a>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-2">Status</label>
                                                    <div className="relative">
                                                        <button onClick={() => { setStatusDropdownOpen(!statusDropdownOpen); setSourceDropdownOpen(false); setProductDropdownOpen(false); }}
                                                            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm hover:border-gray-400 transition">
                                                            <span className={`flex items-center gap-2 ${getStatusCfg(leadDetail?.status || 'new').text}`}>
                                                                <span className={`w-2 h-2 rounded-full ${getStatusCfg(leadDetail?.status || 'new').dot}`} />
                                                                {getStatusCfg(leadDetail?.status || 'new').label}
                                                            </span>
                                                            <ChevronDown size={14} className="text-gray-500" />
                                                        </button>
                                                        {statusDropdownOpen && (
                                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-md z-30 overflow-hidden">
                                                                {STATUS_OPTIONS.map(s => (
                                                                    <button key={s.value} onClick={async () => { await updateLeadField('status', s.value); setStatusDropdownOpen(false); }}
                                                                        className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-100 transition ${s.text}`}>
                                                                        <span className={`w-2 h-2 rounded-full ${s.dot}`} />{s.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-2">Source</label>
                                                    <div className="relative">
                                                        <button onClick={() => { setSourceDropdownOpen(!sourceDropdownOpen); setStatusDropdownOpen(false); setProductDropdownOpen(false); }}
                                                            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm hover:border-gray-400 transition">
                                                            <span className="text-gray-700">
                                                                {SOURCE_OPTIONS.find(s => s.value === leadDetail?.source)
                                                                    ? `${SOURCE_OPTIONS.find(s => s.value === leadDetail.source).icon} ${SOURCE_OPTIONS.find(s => s.value === leadDetail.source).label}`
                                                                    : 'Select source...'}
                                                            </span>
                                                            <ChevronDown size={14} className="text-gray-500" />
                                                        </button>
                                                        {sourceDropdownOpen && (
                                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-md z-30 overflow-hidden">
                                                                {SOURCE_OPTIONS.map(s => (
                                                                    <button key={s.value} onClick={async () => { await updateLeadField('source', s.value); setSourceDropdownOpen(false); }}
                                                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition">
                                                                        <span>{s.icon}</span>{s.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block mb-2">Interested Products</label>
                                                    {(leadDetail?.interestedProducts || []).length > 0 && (
                                                        <div className="flex flex-wrap gap-2 mb-2">
                                                            {leadDetail.interestedProducts.map(p => (
                                                                <div key={p.productId} className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 text-xs">
                                                                    {p.image && <img src={p.image} className="w-5 h-5 rounded object-cover" alt="" />}
                                                                    <span className="text-orange-700 font-medium">{p.name}</span>
                                                                    <button onClick={() => toggleProduct(p)} className="text-gray-500 hover:text-red-600 transition"><X size={12} /></button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="relative">
                                                        <button onClick={() => { setProductDropdownOpen(!productDropdownOpen); setStatusDropdownOpen(false); setSourceDropdownOpen(false); }}
                                                            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm hover:border-gray-400 transition text-gray-500">
                                                            <span className="flex items-center gap-1.5"><Plus size={14} /> Add product...</span>
                                                            <ChevronDown size={14} />
                                                        </button>
                                                        {productDropdownOpen && (
                                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-md z-30 overflow-hidden max-h-56 overflow-y-auto">
                                                                {products.map(p => {
                                                                    const isSel = (leadDetail?.interestedProducts || []).some(ip => ip.productId === p.productId);
                                                                    return (
                                                                        <button key={p.productId} onClick={() => { toggleProduct(p); setProductDropdownOpen(false); }}
                                                                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-100 transition ${isSel ? 'bg-orange-50' : ''}`}>
                                                                            {p.image && <img src={p.image} className="w-8 h-8 rounded object-cover flex-shrink-0" alt="" />}
                                                                            <div className="text-left flex-1 min-w-0">
                                                                                <p className="text-gray-900 font-medium truncate">{p.name}</p>
                                                                                <p className="text-xs text-gray-500">৳{p.price}</p>
                                                                            </div>
                                                                            {isSel && <CheckCircle size={14} className="text-orange-600 flex-shrink-0" />}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-500 font-bold uppercase tracking-wider flex items-center gap-1.5 mb-2">
                                                        <FileText size={11} /> Notes
                                                        <span className="bg-gray-50 text-gray-700 text-[10px] px-1.5 py-0.5 rounded-full">{notes.length}</span>
                                                    </label>
                                                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                                        {notes.length === 0 ? (
                                                            <p className="text-xs text-gray-500 text-center py-4">No notes yet.</p>
                                                        ) : notes.map(n => (
                                                            <div key={n.id} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                                                                <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${n.type === 'call' ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'}`}>
                                                                            {n.type === 'call' ? '📞 Call' : '💬 WhatsApp'}
                                                                        </span>
                                                                        <span className="text-[10px] text-gray-500 truncate max-w-[110px]">{n.createdBy}</span>
                                                                    </div>
                                                                    <span className="text-[10px] text-gray-500 whitespace-nowrap">{formatTime(n.createdAt)}</span>
                                                                </div>
                                                                <p className="text-xs text-gray-700 leading-relaxed">{n.note}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Add Phone Modal ── */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-xl font-bold">Add Phone Number</h2>
                            <button onClick={() => { setShowAddModal(false); setNewPhone(''); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-500"><X size={18} /></button>
                        </div>
                        <div className="mb-5">
                            <label className="text-xs text-gray-500 font-semibold block mb-2">Phone Number (11 digits)</label>
                            <input value={newPhone} onChange={e => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                placeholder="01XXXXXXXXX"
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 font-mono text-lg outline-none focus:border-orange-500"
                                onKeyDown={e => e.key === 'Enter' && handleAddPhone()} />
                            <p className="text-[11px] text-gray-500 mt-1.5">{newPhone.length}/11 digits</p>
                        </div>
                        <button onClick={handleAddPhone} disabled={addingPhone || newPhone.length !== 11}
                            className="w-full bg-orange-400 text-white hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed py-3 rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2">
                            {addingPhone ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                            {addingPhone ? 'Adding...' : 'Add Number'}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Bulk Import Modal ── */}
            {showBulkModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Upload size={18} className="text-orange-600" /> Bulk Import Numbers
                            </h2>
                            {!bulkRunning && (
                                <button onClick={closeBulkModal} className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-500"><X size={18} /></button>
                            )}
                        </div>

                        {bulkResults ? (
                            <div className="flex-1 overflow-y-auto space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-black text-emerald-600">{bulkResults.success.length}</p>
                                        <p className="text-xs text-emerald-700 mt-1 font-semibold">✅ সফল</p>
                                    </div>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-black text-yellow-600">{bulkResults.skipped.length}</p>
                                        <p className="text-xs text-yellow-700 mt-1 font-semibold">⏭ Skipped</p>
                                    </div>
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                                        <p className="text-2xl font-black text-red-600">{bulkResults.failed.length}</p>
                                        <p className="text-xs text-red-700 mt-1 font-semibold">❌ Failed</p>
                                    </div>
                                </div>
                                {bulkResults.failed.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2">Failed Numbers</p>
                                        <div className="bg-gray-50 rounded-xl divide-y divide-gray-200 max-h-40 overflow-y-auto">
                                            {bulkResults.failed.map((f, i) => (
                                                <div key={i} className="flex items-center justify-between px-4 py-2 text-xs">
                                                    <span className="font-mono text-gray-900">{f.phone}</span>
                                                    <span className="text-red-600 truncate ml-4 max-w-[200px]">{f.reason}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {bulkResults.skipped.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-2">Skipped (Already Exist)</p>
                                        <div className="bg-gray-50 rounded-xl divide-y divide-gray-200 max-h-32 overflow-y-auto">
                                            {bulkResults.skipped.map((f, i) => (
                                                <div key={i} className="px-4 py-2 text-xs font-mono text-yellow-600">{f.phone}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <button onClick={closeBulkModal} className="w-full bg-orange-400 text-white hover:bg-orange-500 py-3 rounded-xl font-semibold text-sm transition">
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="mb-3">
                                    <label className="text-xs text-gray-500 font-semibold block mb-2">
                                        নাম্বারগুলো Paste করুন
                                        <span className="text-gray-500 font-normal ml-1">(যেকোনো format — comma, newline, +880 সবই চলবে)</span>
                                    </label>
                                    <textarea
                                        value={bulkText}
                                        onChange={e => setBulkText(e.target.value)}
                                        disabled={bulkRunning}
                                        placeholder={`+880 1981-207985, +880 1707-384005\n01812345678\n...`}
                                        rows={8}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 text-sm font-mono outline-none focus:border-orange-500 resize-none placeholder-gray-400 disabled:opacity-50"
                                    />
                                    {previewPhones.length > 0 && (
                                        <p className="text-xs text-orange-600 mt-1.5 font-semibold">
                                            ✓ {previewPhones.length}টি নাম্বার পাওয়া গেছে — {previewBatches}টি batch এ পাঠানো হবে
                                        </p>
                                    )}
                                </div>

                                {bulkRunning && (
                                    <div className="mb-4">
                                        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                                            <span className="text-orange-700 font-medium truncate mr-2">{bulkStatusMsg || 'Processing...'}</span>
                                            <span className="font-mono text-gray-900 whitespace-nowrap">Batch {bulkProgress} / {bulkTotal}</span>
                                        </div>
                                        <div className="h-2.5 bg-gray-50 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-orange-500 text-white rounded-full transition-all duration-300"
                                                style={{ width: `${bulkTotal ? (bulkProgress / bulkTotal) * 100 : 0}%` }}
                                            />
                                        </div>
                                        <p className="text-[11px] text-gray-500 mt-1">এই window বন্ধ করবেন না।</p>
                                    </div>
                                )}

                                <div className="flex gap-3 mt-auto">
                                    <button onClick={closeBulkModal} disabled={bulkRunning}
                                        className="flex-1 py-3 bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-40 rounded-xl font-semibold text-sm transition">
                                        Cancel
                                    </button>
                                    <button onClick={handleBulkImport} disabled={bulkRunning || previewPhones.length === 0}
                                        className="flex-1 py-3 bg-orange-400 text-white hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2">
                                        {bulkRunning
                                            ? <><RefreshCw size={14} className="animate-spin" /> Running...</>
                                            : <><Upload size={14} /> Import {previewPhones.length > 0 ? `${previewPhones.length} Numbers` : ''}</>}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── Edit Phone Modal ── */}
            {editModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <Pencil size={18} className="text-amber-600" /> Edit Phone Number
                            </h2>
                            <button onClick={() => { setEditModal(null); setEditPhoneVal(''); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-500"><X size={18} /></button>
                        </div>
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
                            <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700 leading-relaxed">
                                এই নাম্বার <strong>মাত্র একবারই</strong> edit করা যাবে। Save করার পরে আর change করা যাবে না। সব notes preserve থাকবে।
                            </p>
                        </div>
                        <div className="mb-3">
                            <label className="text-xs text-gray-500 font-semibold block mb-2">Current Number</label>
                            <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl font-mono text-gray-500 text-sm">{editModal.phone}</div>
                        </div>
                        <div className="mb-5">
                            <label className="text-xs text-gray-500 font-semibold block mb-2">New Number (11 digits)</label>
                            <input value={editPhoneVal} onChange={e => setEditPhoneVal(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                placeholder="01XXXXXXXXX"
                                className="w-full px-4 py-3 bg-gray-50 border border-amber-200 rounded-xl text-gray-900 font-mono text-lg outline-none focus:border-amber-500"
                                onKeyDown={e => e.key === 'Enter' && handleEditPhone()} />
                            <p className="text-[11px] text-gray-500 mt-1.5">{editPhoneVal.length}/11 digits</p>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => { setEditModal(null); setEditPhoneVal(''); }}
                                className="flex-1 py-3 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-xl font-semibold text-sm transition">Cancel</button>
                            <button onClick={handleEditPhone} disabled={savingEdit || editPhoneVal.length !== 11 || editPhoneVal === editModal.phone}
                                className="flex-1 py-3 bg-orange-400 text-white hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl font-semibold text-sm transition flex items-center justify-center gap-2">
                                {savingEdit ? <RefreshCw size={14} className="animate-spin" /> : <Pencil size={14} />}
                                {savingEdit ? 'Saving...' : 'Save Change'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Note Modal ── */}
            {noteModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-lg">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                {noteModal.type === 'call'
                                    ? <><Phone size={18} className="text-green-600" /> Call Note</>
                                    : <><MessageCircle size={18} className="text-orange-600" /> WhatsApp Note</>}
                            </h2>
                            <button onClick={() => { setNoteModal(null); setNoteText(''); }} className="p-1.5 hover:bg-gray-100 rounded-lg transition text-gray-500"><X size={18} /></button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Note for <span className="font-mono text-gray-900 font-bold">{selectedLead?.phone}</span> · {noteModal.type} count +1
                        </p>
                        <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                            placeholder="Write your note here..." rows={4}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 text-sm outline-none focus:border-orange-500 resize-none placeholder-gray-400" />
                        <div className="flex gap-3 mt-4">
                            <button onClick={() => { setNoteModal(null); setNoteText(''); }}
                                className="flex-1 py-2.5 bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-xl text-sm font-semibold transition">Cancel</button>
                            <button onClick={handleNoteSubmit} disabled={savingNote || !noteText.trim()}
                                className="flex-1 py-2.5 bg-orange-400 text-white hover:bg-orange-500 disabled:opacity-40 rounded-xl text-sm font-semibold transition flex items-center justify-center gap-2">
                                {savingNote ? <RefreshCw size={14} className="animate-spin" /> : null}
                                Save Note
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
